// @oasis-permission-inherits: /users (users:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { internalTestAccountsQuery } from "~/modules/lookup/queries";

export const Route = createFileRoute("/_dash/users/internal-test-accounts")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(internalTestAccountsQuery.queryOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const { data } = internalTestAccountsQuery.useSuspenseQuery();
  const [query, setQuery] = useState("");
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "inactive">("all");

  const products = useMemo(
    () =>
      Array.from(
        new Set(
          data.accounts
            .map((account) => account.product)
            .filter((value): value is string => Boolean(value))
        )
      ).sort(),
    [data.accounts]
  );
  const filteredAccounts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return data.accounts.filter((account) => {
      const matchesQuery =
        normalizedQuery.length === 0 ||
        [account.accountKey, account.email, account.userId, account.name, account.displayName]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(normalizedQuery));
      const matchesProduct = !product || account.product === product;
      const accountState = getAccountState(account);
      const isInactive = accountState === "inactive";
      const matchesStatus = status === "all" || (status === "active" ? !isInactive : isInactive);

      return matchesQuery && matchesProduct && matchesStatus;
    });
  }, [data.accounts, product, query, status]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Internal Test Accounts</h1>
          <p className="text-sm text-muted-foreground">{filteredAccounts.length} accounts</p>
        </div>
        <Link to="/users" className="rounded-md border px-3 py-2 text-sm hover:bg-accent">
          User lookup
        </Link>
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px_160px]">
        <input
          className="rounded-md border bg-background px-3 py-2"
          placeholder="Filter by account key, email, name, or user ID"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <select
          className="rounded-md border bg-background px-3 py-2"
          value={product}
          onChange={(event) => setProduct(event.target.value)}
        >
          <option value="">All products</option>
          {products.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border bg-background px-3 py-2"
          value={status}
          onChange={(event) => setStatus(event.target.value as typeof status)}
        >
          <option value="all">All states</option>
          <option value="active">Active</option>
          <option value="inactive">Deleted/disabled</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="px-3 py-2 font-medium">Account</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Product</th>
              <th className="px-3 py-2 font-medium">Orgs</th>
              <th className="px-3 py-2 font-medium">Last sign-in</th>
              <th className="px-3 py-2 font-medium">State</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.map((account) => (
              <tr key={account.accountKey} className="border-b last:border-b-0">
                <td className="px-3 py-2">
                  <div className="font-medium">{account.accountKey}</div>
                  <div className="text-xs text-muted-foreground">
                    {account.purpose ?? account.owner}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {account.userId ? (
                    <Link
                      to="/users/view/$userId"
                      params={{ userId: account.userId }}
                      className="font-medium text-primary underline-offset-4 hover:underline"
                    >
                      {account.email ?? account.name ?? account.userId}
                    </Link>
                  ) : (
                    <span>{account.email ?? account.name ?? "No user"}</span>
                  )}
                  {account.userId && (
                    <div className="font-mono text-xs text-muted-foreground">{account.userId}</div>
                  )}
                </td>
                <td className="px-3 py-2">{account.product ?? "-"}</td>
                <td className="px-3 py-2">
                  <div>{account.primaryOrgId ?? "-"}</div>
                  {account.orgIds && account.orgIds.length > 1 && (
                    <div className="text-xs text-muted-foreground">
                      {account.orgIds.length} orgs
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {account.lastSignInAt ??
                    account.lastLoginAt ??
                    account.lastAuthMintedAt ??
                    account.lastSeenAt ??
                    "-"}
                </td>
                <td className="px-3 py-2">{getAccountState(account)}</td>
              </tr>
            ))}
            {filteredAccounts.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-muted-foreground" colSpan={6}>
                  No internal test accounts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getAccountState(account: {
  deletedAt?: string | null;
  disabledAt?: string | null;
  status?: string | null;
}) {
  if (
    account.deletedAt ||
    account.disabledAt ||
    account.status === "deleted" ||
    account.status === "disabled"
  ) {
    return "inactive";
  }

  return account.status ?? "active";
}
